import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useConfig } from '../config/ConfigContext';
import { isCommunityFeatureEnabled } from '../utils/featureHelpers';

/**
 * DesktopLayout Component
 * Provides a layout structure for desktop views
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @returns {JSX.Element}
 */
const DesktopLayout = ({ children }) => {
  const { config, theme, loading, error } = useConfig(); // Get config and theme from context
  const location = useLocation(); // Get location object
  const navigate = useNavigate(); // Get navigate function
  
  // Handle Home link click - scroll to top if already on homepage
  const handleHomeClick = (e) => {
    e.preventDefault();
    if (location.pathname === '/') {
      // Already on homepage, scroll to top
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      // Navigate to homepage
      navigate(`/${location.search}`);
    }
  };

  // Support both new and legacy config field structures
  const organization = config?.organization || {};

  // Determine logo source in order of priority
  const logoSrc =
    organization.logo || // legacy nested
    config.logo_url || // v3 schema
    config.logo || // v2 schema
    '/logos/dez-logo.png'; // fallback

  // Determine center name
  const centerName =
    organization.name || // legacy nested
    config.name || // v3 schema
    'Center Name'; // fallback

  return (
    <div className="desktop-layout" style={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
    }}>
      <header style={{
        backgroundColor: theme.colors.primary,
        color: 'white',
        padding: '12px 24px',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          maxWidth: '1200px',
          margin: '0 auto',
          width: '100%',
        }}>
          <a 
            href={`/${location.search}`}
            onClick={handleHomeClick}
            style={{ 
              textDecoration: 'none', 
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '25px', // Increased gap between logo and text
              cursor: 'pointer'
            }}>
            {/* Logo — white bg ensures transparent logos stay visible on dark headers */}
            <div style={{
              width: '45px',
              height: '45px',
              borderRadius: '50%',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'white',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
              flexShrink: 0
            }}>
              <img 
                src={logoSrc} // Use dynamic logo source
                alt={centerName} // Use dynamic alt text
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </div>
            <h1 style={{ 
              margin: 0, 
              fontSize: '1.4rem', 
              whiteSpace: 'nowrap' // Prevent text from wrapping
            }}>{centerName}</h1> {/* Use dynamic center name */}
          </a>
          <nav>
            <ul style={{
              display: 'flex',
              listStyle: 'none',
              margin: 0,
              padding: 0,
              gap: '8px',
              flexWrap: 'nowrap',
              alignItems: 'center',
            }}>
              <li>
                <a 
                  href={`/${location.search}`}
                  onClick={handleHomeClick}
                  style={{ 
                    color: 'white', 
                    textDecoration: 'none',
                    fontWeight: '500',
                    padding: '6px 10px',
                    borderRadius: '4px',
                    whiteSpace: 'nowrap',
                    fontSize: '14px',
                    transition: 'background-color 0.2s ease',
                    backgroundColor: location.pathname === '/' ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                    borderBottom: location.pathname === '/' ? '2px solid white' : '2px solid transparent',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    if (location.pathname !== '/') {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (location.pathname !== '/') {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}>
                  Home
                </a>
              </li>
              <li>
                <Link to={`/events${location.search}`} style={{
                  color: 'white',
                  textDecoration: 'none',
                  fontWeight: '500',
                  padding: '6px 10px',
                  borderRadius: '4px',
                  whiteSpace: 'nowrap',
                  fontSize: '14px',
                  transition: 'background-color 0.2s ease',
                  backgroundColor: location.pathname === '/events' ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                  borderBottom: location.pathname === '/events' ? '2px solid white' : '2px solid transparent'
                }}
                onMouseEnter={(e) => {
                  if (location.pathname !== '/events') {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (location.pathname !== '/events') {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}>
                  Events
                </Link>
              </li>
              {(config?.community_features?.calendar?.enabled === true || config?.community_features?.calendar === true) && (
                <li>
                  <Link to={`/community/calendar${location.search}`} style={{ 
                    color: 'white', 
                    textDecoration: 'none',
                    fontWeight: '500',
                    padding: '6px 10px',
                    borderRadius: '4px',
                    whiteSpace: 'nowrap',
                    fontSize: '14px',
                    transition: 'background-color 0.2s ease',
                    backgroundColor: location.pathname === '/community/calendar' ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                    borderBottom: location.pathname === '/community/calendar' ? '2px solid white' : '2px solid transparent'
                  }}
                  onMouseEnter={(e) => {
                    if (location.pathname !== '/community/calendar') {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (location.pathname !== '/community/calendar') {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}>
                    Calendar
                  </Link>
                </li>
              )}
              {config?.community_features?.announcements && (
                <li>
                  <Link to={`/community/announcements${location.search}`} style={{ 
                    color: 'white', 
                    textDecoration: 'none',
                    fontWeight: '500',
                    padding: '6px 10px',
                    borderRadius: '4px',
                    whiteSpace: 'nowrap',
                    fontSize: '14px',
                    transition: 'background-color 0.2s ease',
                    backgroundColor: location.pathname === '/community/announcements' ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                    borderBottom: location.pathname === '/community/announcements' ? '2px solid white' : '2px solid transparent'
                  }}
                  onMouseEnter={(e) => {
                    if (location.pathname !== '/community/announcements') {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (location.pathname !== '/community/announcements') {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}>
                    Announcements
                  </Link>
                </li>
              )}
              {config?.publications_sheet_id && (
                <li>
                  <Link to={`/publications${location.search}`} style={{
                    color: 'white',
                    textDecoration: 'none',
                    fontWeight: '500',
                    padding: '6px 10px',
                    borderRadius: '4px',
                    whiteSpace: 'nowrap',
                    fontSize: '14px',
                    transition: 'background-color 0.2s ease',
                    backgroundColor: location.pathname === '/publications' ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                    borderBottom: location.pathname === '/publications' ? '2px solid white' : '2px solid transparent'
                  }}
                  onMouseEnter={(e) => {
                    if (location.pathname !== '/publications') {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (location.pathname !== '/publications') {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}>
                    Publications
                  </Link>
                </li>
              )}
              {config?.lecture_clips_enabled && (
                <li>
                  <Link to={`/lecture-clips${location.search}`} style={{
                    color: 'white',
                    textDecoration: 'none',
                    fontWeight: '500',
                    padding: '6px 10px',
                    borderRadius: '4px',
                    whiteSpace: 'nowrap',
                    fontSize: '14px',
                    transition: 'background-color 0.2s ease',
                    backgroundColor: location.pathname === '/lecture-clips' ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                    borderBottom: location.pathname === '/lecture-clips' ? '2px solid white' : '2px solid transparent'
                  }}
                  onMouseEnter={(e) => {
                    if (location.pathname !== '/lecture-clips') {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (location.pathname !== '/lecture-clips') {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}>
                    Lecture Clips
                  </Link>
                </li>
              )}
              {isCommunityFeatureEnabled(config, 'tax_docs') && (
                <li>
                  <Link to={`/community/tax-docs${location.search}`} style={{
                    color: 'white',
                    textDecoration: 'none',
                    fontWeight: '500',
                    padding: '6px 10px',
                    borderRadius: '4px',
                    whiteSpace: 'nowrap',
                    fontSize: '14px',
                    transition: 'background-color 0.2s ease',
                    backgroundColor: location.pathname === '/community/tax-docs' ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                    borderBottom: location.pathname === '/community/tax-docs' ? '2px solid white' : '2px solid transparent'
                  }}
                  onMouseEnter={(e) => {
                    if (location.pathname !== '/community/tax-docs') {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (location.pathname !== '/community/tax-docs') {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}>
                    Tax Docs
                  </Link>
                </li>
              )}
              {config?.about_us_enabled && (
                <li>
                  <Link to={`/about${location.search}`} style={{
                    color: 'white',
                    textDecoration: 'none',
                    fontWeight: '500',
                    padding: '6px 10px',
                    borderRadius: '4px',
                    whiteSpace: 'nowrap',
                    fontSize: '14px',
                    transition: 'background-color 0.2s ease',
                    backgroundColor: location.pathname === '/about' ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                    borderBottom: location.pathname === '/about' ? '2px solid white' : '2px solid transparent'
                  }}
                  onMouseEnter={(e) => {
                    if (location.pathname !== '/about') {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (location.pathname !== '/about') {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}>
                    About
                  </Link>
                </li>
              )}
              {config?.support_enabled && (
                <li>
                  <Link to={`/support${location.search}`} style={{ 
                    color: 'white', 
                    textDecoration: 'none',
                    fontWeight: '500',
                    padding: '6px 10px',
                    borderRadius: '4px',
                    whiteSpace: 'nowrap',
                    fontSize: '14px',
                    transition: 'background-color 0.2s ease',
                    backgroundColor: location.pathname === '/support' ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                    borderBottom: location.pathname === '/support' ? '2px solid white' : '2px solid transparent'
                  }}
                  onMouseEnter={(e) => {
                    if (location.pathname !== '/support') {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (location.pathname !== '/support') {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}>
                    Support
                  </Link>
                </li>
              )}
            </ul>
          </nav>
        </div>
      </header>

      <main
        className={theme.isDarkBackground ? 'dark-content-bg' : ''}
        style={{
          flex: 1,
          width: '100%',
          backgroundColor: theme.colors.background,
          '--heading-color': theme.colors.headingColor,
          '--subtitle-color': theme.colors.subtitleColor,
          '--link-color': theme.colors.linkColor,
          '--text-color': theme.colors.text,
          '--text-secondary': theme.colors.cardTextSecondary || '#666666',
          '--card-bg': theme.colors.cardBg,
          '--page-bg': theme.colors.background,
          '--border-color': theme.isDarkBackground ? 'rgba(255,255,255,0.12)' : '#e0e0e0',
          '--scrollbar-thumb': `${theme.colors.primary}40`,
          '--scrollbar-thumb-hover': `${theme.colors.primary}70`,
        }}
      >
        {children}
      </main>

      <footer style={{
        backgroundColor: theme.colors.primary,
        color: 'white',
        padding: '36px 24px',
        marginTop: 'auto',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}>
          <Link to={`/${location.search}`} style={{ 
            textDecoration: 'none', 
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '16px'
          }}>
            {/* Logo in footer */}
            <div style={{
              width: '32px',
              height: '32px',
              backgroundColor: 'white',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              fontSize: '16px',
              color: theme.colors.primary
            }}>
              {(config?.code || centerName.substring(0, 2)).toUpperCase()}
            </div>
            <span style={{ fontSize: '1.1rem', fontWeight: '600' }}>
              {centerName}
            </span>
          </Link>
          <p style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.7)', margin: 0 }}>
            {config.message || 'Providing lectures and educational content'}
          </p>
          <p style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.7)', margin: 0 }}>
            &copy; {new Date().getFullYear()} {centerName} - All rights reserved
          </p>
          <Link
            to={`/feedback${location.search}`}
            style={{
              fontSize: '0.8rem',
              color: 'rgba(255, 255, 255, 0.7)',
              textDecoration: 'underline',
              transition: 'color 0.2s ease',
              marginTop: '8px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = 'rgba(255, 255, 255, 1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)';
            }}
          >
            Share Feedback
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default DesktopLayout;
